import { apiClient } from '@api/client';
import { nonNull } from '@helpers/non-null';
import type { InstalledIntegrationDetailsProps } from '@pages/AdminSettings/components/IntegrationTab/constants';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Divider,
  Form,
  Input,
  Select,
  Switch,
  Tooltip,
  Typography
} from 'antd';
import React, { Fragment, useState } from 'react';
import CopyableInput from '@components/CopyableInput/CopyableInput';
import { LtiClientRoleDto } from '@api/Api';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useOrganization } from '@components/OrganizationBoundary';

const { Title, Paragraph } = Typography;

interface RoleMapping {
  ltiRole: string;
  role: 'organization://admin' | LtiClientRoleDto['workspaceRole'];
}

export default function LtiInstalledDetails({
  integration
}: InstalledIntegrationDetailsProps) {
  const { data, refetch } = useQuery({
    suspense: true,
    queryKey: ['lti', 'registration-info', integration.id],
    queryFn: async () => {
      const { data } =
        await apiClient.integration.ltiIntegrationControllerRegistrationInfo({
          integrationId: integration.id
        });
      return data;
    }
  });
  const { clientId, roles } = nonNull(data);
  const [roleMappingForm] = Form.useForm<{
    roles: RoleMapping[];
    trustEmail: boolean;
  }>();

  const updateRoleMapping = useMutation({
    mutationFn: async ({
      trustEmail,
      roles
    }: {
      trustEmail: boolean;
      roles: RoleMapping[];
    }) => {
      const { data } =
        await apiClient.integration.ltiIntegrationControllerUpdateRoles({
          integrationId: integration.id,
          trustEmail,
          roles: roles.map((x) => ({
            ltiRole: x.ltiRole,
            organizationRole:
              x.role === 'organization://admin'
                ? x.role
                : 'organization://workspace-member',
            workspaceRole:
              x.role === 'organization://admin'
                ? 'workspace://team-lead'
                : x.role
          }))
        });
      return data;
    },
    onSettled: async () => {
      await refetch();
      setIsTouched(false);
      setTimeout(() => {
        roleMappingForm.resetFields();
      }, 0);
    }
  });

  const [isTouched, setIsTouched] = useState(false);
  const { currentOrganization } = useOrganization(true);
  const canTrustEmail = !!currentOrganization.entity?.ownDomains?.length;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Title level={3}>{integration.name || 'LTI 1.3'}</Title>
      <Alert
        type="info"
        showIcon
        description={
          <>
            <Paragraph>
              Your LMS assigned the following <strong>Client ID</strong>:
            </Paragraph>
            <CopyableInput
              readOnly
              size="small"
              value={clientId}
              style={{ marginBottom: 16 }}
            />
          </>
        }
      />
      <Divider />
      <Title level={3}>Choose How LMS Users Join StreamWork</Title>

      <Form
        form={roleMappingForm}
        layout="vertical"
        initialValues={{
          trustEmail: !!data?.trustEmail && canTrustEmail,
          roles: roles.map((x) => ({
            ltiRole: x.ltiRole,
            role:
              x.organizationRole === 'organization://admin'
                ? x.organizationRole
                : x.workspaceRole
          }))
        }}
        style={{ marginBottom: 16 }}
        onFinish={(values) => updateRoleMapping.mutateAsync(values)}
        onFieldsChange={() => setIsTouched(true)}
      >
        <Form.Item
          label="Trust institution email addresses"
          extra={
            <div style={{ maxWidth: '60ch' }}>
              When enabled, StreamWork will trust email addresses provided by
              the LMS for user identification. This allows new users coming from
              the LMS to skip the email verification step.
            </div>
          }
        >
          <Tooltip
            overlayClassName="toggle_favorite_overlay"
            placement="right"
            title={
              canTrustEmail ? null : (
                <>
                  Please contact
                  <br />
                  <a href="mailto:support@streamwork.com">
                    support@streamwork.com
                  </a>
                  <br />
                  to enable this feature
                </>
              )
            }
          >
            <span>
              <Form.Item noStyle name="trustEmail" valuePropName="checked">
                <Switch disabled={!canTrustEmail} />
              </Form.Item>
            </span>
          </Tooltip>
        </Form.Item>
        <Divider />
        <Paragraph style={{ maxWidth: 600 }}>
          Specify how incoming <strong>LTI roles</strong> should be translated
          into <strong>StreamWork roles</strong>. Default suggestions are
          provided, but you can add, edit, or remove mappings below.
        </Paragraph>
        <Form.List name="roles">
          {(fields, { add, remove }) => (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr auto',
                columnGap: 16
              }}
            >
              {fields.map((field) => (
                <Fragment key={field.key}>
                  <Form.Item
                    {...field}
                    name={[field.name, 'ltiRole']}
                    rules={[
                      { required: true, message: 'LTI role is required' }
                    ]}
                  >
                    <Input placeholder="LTI role (e.g., Learner)" />
                  </Form.Item>
                  <Form.Item
                    {...field}
                    name={[field.name, 'role']}
                    rules={[
                      {
                        required: true,
                        message: 'StreamWork role is required'
                      }
                    ]}
                  >
                    <Select
                      placeholder="StreamWork role"
                      options={[
                        { value: 'organization://admin', label: 'Admin' },
                        {
                          value: 'workspace://team-lead',
                          label: 'Workspace lead'
                        },
                        {
                          value: 'workspace://member',
                          label: 'Workspace member'
                        },
                        {
                          value: 'workspace://contributor',
                          label: 'Contributor'
                        }
                      ]}
                    />
                  </Form.Item>
                  {fields.length > 1 && (
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => remove(field.name)}
                    />
                  )}
                </Fragment>
              ))}
              <Button
                type="dashed"
                onClick={() => add({ ltiRole: '', swRole: '' })}
                icon={<PlusOutlined />}
                style={{ gridColumnStart: 1, gridColumnEnd: -2 }}
              >
                Add mapping
              </Button>
            </div>
          )}
        </Form.List>
      </Form>
      <div
        style={{
          display: 'flex',
          marginTop: 'auto',
          justifyContent: 'flex-end'
        }}
      >
        <Button
          type="primary"
          loading={updateRoleMapping.isLoading}
          onClick={() => roleMappingForm.submit()}
          disabled={!isTouched}
        >
          Update Settings
        </Button>
      </div>
    </div>
  );
}
